import React from 'react';
import {
  Image, FileText, Video, Music, Code, Mail, HelpCircle, Hash, FileAudio, Share2,
  CheckCircle, XCircle, Clock, AlertCircle, Edit2, Plus, Trash2,
  Monitor, Megaphone, Layout, Film, FileImage, MessageSquare, FileSpreadsheet, Globe, File
} from 'lucide-react';

interface AssetApproval {
  approver_type: string;
  status: string;
}

interface Asset {
  id: number;
  asset_type: string;
  file_name: string;
  asset_approvals?: AssetApproval[];
}

interface AssetsListProps {
  title?: string;
  assets: Asset[];
  selectedAsset: Asset | null;
  onAssetSelect: (asset: Asset) => void;
  selectedTab?: string;
  onTabChange?: (tab: string) => void;
  onAddAsset?: () => void;
  onRequestAsset?: () => void;
  reviewerType: 'account_manager' | 'client';
  onApproveAsset?: (assetId: number) => void;
  onRejectAsset?: (assetId: number) => void;
  onEditAsset?: (assetId: number) => void;
  onDeleteAsset?: (assetId: number) => void;
  onApproveAll?: () => void;
  onAddAssetFeedback?: (assetId: number, feedback: string) => void;
  assetFeedback?: Record<number, string[]>;
}

function AssetsList({
  title,
  assets,
  selectedAsset,
  onAssetSelect,
  selectedTab = 'all',
  onTabChange,
  onAddAsset,
  onRequestAsset,
  reviewerType,
  onApproveAsset,
  onRejectAsset,
  onEditAsset,
  onDeleteAsset,
  onApproveAll,
  onAddAssetFeedback,
  assetFeedback = {}
}: AssetsListProps) {
  const getAssetIcon = (type: string) => {
    const iconClass = "w-3.5 h-3.5";
    switch (type) {
      case 'image':
      case 'infographic':
        return <Image className={iconClass} />;
      case 'video':
      case 'video_ad':
        return <Video className={iconClass} />;
      case 'pdf':
        return <File className={`${iconClass} text-red-600`} />;
      case 'page':
      case 'copy':
        return <FileText className={iconClass} />;
      case 'meta':
        // For campaign JSON files, show a more appropriate icon
        return <FileText className={iconClass} />;
      case 'seo':
        return <Hash className={iconClass} />;
      case 'schema':
        return <Code className={iconClass} />;
      case 'podcast':
      case 'song':
        return <Music className={iconClass} />;
      case 'transcription':
        return <FileAudio className={iconClass} />;
      case 'social':
      case 'social_media':
        return <Share2 className={iconClass} />;
      case 'email':
        return <Mail className={iconClass} />;
      case 'faq':
        return <HelpCircle className={iconClass} />;
      // Digital Ads specific icons
      case 'ads':
      case 'ad':
      case 'ad_creative':
      case 'native_ad':
        return <Megaphone className={iconClass} />;
      case 'ad_copy':
      case 'search_ad':
        return <FileText className={iconClass} />;
      case 'ad_banner':
      case 'responsive_display':
        return <Layout className={iconClass} />;
      case 'carousel':
      case 'collection_ad':
        return <FileImage className={iconClass} />;
      case 'story_ad':
        return <Film className={iconClass} />;
      case 'shopping_ad':
        return <FileSpreadsheet className={iconClass} />;
      case 'message_ad':
        return <MessageSquare className={iconClass} />;
      case 'document_ad':
        return <FileText className={iconClass} />;
      case 'html5_creative':
        return <Globe className={iconClass} />;
      default:
        return <Monitor className={iconClass} />;
    }
  };

  const getAssetStatus = (asset: Asset) => {
    if (!asset.asset_approvals || asset.asset_approvals.length === 0) {
      return 'pending';
    }
    const currentApproval = asset.asset_approvals.find(
      a => a.approver_type === reviewerType
    );
    return currentApproval?.status || 'pending';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-3.5 h-3.5 text-red-500" />;
      case 'needs_edit':
        return <AlertCircle className="w-3.5 h-3.5 text-yellow-500" />;
      default:
        return <Clock className="w-3.5 h-3.5 text-gray-400" />;
    }
  };

  const isTextAsset = (type: string) => {
    return ['page', 'copy', 'meta', 'seo', 'email', 'social', 'social_media', 'faq'].includes(type);
  };

  // Calculate counts for each tab
  const getTabCount = (tabId: string): number => {
    if (tabId === 'all') return assets.length;

    const typeMap: Record<string, string[]> = {
      'content': ['page', 'copy'],
      'documents': ['pdf'],
      'images': ['image', 'infographic'],
      'video': ['video', 'video_ad'],
      'seo': ['seo'], // Removed 'meta' - handled separately
      'social': ['social'],
      'email': ['email'],
      'faq': ['faq'],
      'ads': ['ads', 'ad', 'ad_creative', 'ad_banner', 'responsive_display', 'native_ad',
              'html5_creative', 'ad_copy', 'search_ad', 'shopping_ad', 'carousel',
              'story_ad', 'collection_ad', 'message_ad', 'document_ad', 'video_ad']
    };

    const allowedTypes = typeMap[tabId] || [];

    return assets.filter(asset => {
      // Special handling for 'meta' type files
      if (asset.asset_type === 'meta') {
        // Campaign JSON files count in ads tab
        if (tabId === 'ads' && asset.file_name?.includes('campaign')) {
          return true;
        }
        // Regular meta files count in SEO tab
        if (tabId === 'seo' && !asset.file_name?.includes('campaign')) {
          return true;
        }
        return false;
      }

      // Regular counting for other types
      return allowedTypes.includes(asset.asset_type);
    }).length;
  };

  // Generate tabs dynamically based on available assets
  const generateTabs = () => {
    const baseTabs = [
      { id: 'all', label: 'All' },
      { id: 'content', label: 'Content' },
      { id: 'images', label: 'Images' },
      { id: 'video', label: 'Video' },
      { id: 'seo', label: 'SEO' },
      { id: 'social', label: 'Social' },
      { id: 'email', label: 'Email' },
      { id: 'faq', label: 'FAQ' },
      { id: 'ads', label: 'Ads' }
    ];

    return baseTabs.map(tab => ({
      ...tab,
      count: getTabCount(tab.id),
      label: tab.label + (getTabCount(tab.id) > 0 ? ` (${getTabCount(tab.id)})` : '')
    })).filter(tab => tab.count > 0 || tab.id === 'all');
  };

  const tabs = generateTabs();

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-gray-200">
        <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">
          {title || 'Assets'}
        </h4>

        {/* Tabs */}
        {onTabChange && (
          <div className="flex flex-wrap gap-1 mb-3">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  selectedTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Assets List */}
      <div className="flex-1 overflow-y-auto p-3">
        <div className="space-y-1">
          {assets.map((asset) => {
            const status = getAssetStatus(asset);
            const isText = isTextAsset(asset.asset_type);
            const isSelected = selectedAsset?.id === asset.id;

            return (
              <div key={asset.id} className="group">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onAssetSelect(asset)}
                    className={`flex-1 text-left px-2 py-1.5 rounded-md text-sm transition-all flex items-center ${
                      isSelected
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'hover:bg-white border border-transparent'
                    }`}
                  >
                    <span className="flex-1 truncate text-xs" title={asset.file_name}>
                      {(() => {
                        // Remove file extension and limit to 20 characters
                        const nameWithoutExt = asset.file_name.replace(/\.[^/.]+$/, '');
                        return nameWithoutExt.length > 20
                          ? nameWithoutExt.slice(0, 20) + '...'
                          : nameWithoutExt;
                      })()}
                    </span>
                    <div className="flex items-center gap-1.5 ml-auto">
                      {isText && <Edit2 className="w-3 h-3 text-gray-400" />}
                      {getAssetIcon(asset.asset_type)}
                      {getStatusIcon(status)}
                    </div>
                  </button>
                  {onDeleteAsset && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Are you sure you want to delete "${asset.file_name}"?`)) {
                          onDeleteAsset(asset.id);
                        }
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-all"
                      title="Delete asset"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  )}
                </div>

              </div>
            );
          })}

          {/* Add Asset Button */}
          {onAddAsset && (
            <button
              onClick={onAddAsset}
              className="w-full text-left px-2 py-1.5 rounded-md text-sm transition-all flex items-center gap-2 border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-600 hover:text-blue-600 mt-2"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="flex-1 text-xs">Add Asset</span>
            </button>
          )}

          {/* Request Asset Button */}
          {onRequestAsset && (
            <button
              onClick={onRequestAsset}
              className="w-full text-left px-2 py-1.5 rounded-md text-sm transition-all flex items-center gap-2 border-2 border-dashed border-purple-300 hover:border-purple-400 hover:bg-purple-50 text-purple-600 hover:text-purple-700 mt-1"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="flex-1 text-xs">Request Asset</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AssetsList;